import EngineModule from '@/store/engine/modules/EngineModule';
import { Store } from '@/store/mainStore';
import { AgentService, ScanSubtype, ScanUploadURL, ScannerType, SessionStatusEnum } from '@/store/engine/openapi/generated';

function useSoftwareMock() {
	return !!window.appConfig.VUE_APP_MOCK_USB_AGENT;
}

export default class HPAgentModule extends EngineModule {
	private agentAPIPort = 5000;
	private deviceURI: string = 'localhost';

	public agentStatus: HPAgentStatus = HPAgentStatus.DISCONNECTED;
	private socket?: WebSocket;

	public devices: Array<Device> = [];
	public currentDevice?: Device;
	private sessionUUID?: string;
	public lastValidSession?: string;

	constructor(store: Store) {
		super(store);
	}

	public async setLastValidScanningSession(sessionID: string) {
		this.lastValidSession = sessionID;
	}

	public async setCurrentDevice(device: Device) {
		this.currentDevice = device;
	}

	public setAddress(ip: string, port: number) {
		this.deviceURI = ip;
		this.agentAPIPort = port;
	}

	public async debugPerformMockScan(scanType: string) {
		const isLeftFoot = scanType === 'leftscan';
		const subtype1 = isLeftFoot ? ScanSubtype.SCAN_SUBTYPE_FLAT_LEFT_FOOT : ScanSubtype.SCAN_SUBTYPE_FLAT_RIGHT_FOOT;
		let createUploadResponse: ScanUploadURL;
		try {
			createUploadResponse = await AgentService.agentControllerCreateScanUploadUrl({
				session_id: this.lastValidSession!,
				subtype: subtype1,
				metaData: {
					scanner_type: ScannerType.MOCK_SCANNER,
					scan_mode: 'NON_WEIGHTED',
					body_part: 'FOOT',
					side: 'BOTH',
					product: 'INSOLE',
					encoding: 'draco',
					foot: isLeftFoot ? 'left' : 'right',
					serial: 'MockData',
					firmware: '1.0.0',
					origin: 'Software Mock',
					timestamp: new Date().toISOString()
				}
			});
		} catch (e) {
			console.log(e);
			return { error: 'Error creating mock scan upload' };
		}
		// upload file
		const dataResponse = await fetch(`/assets/mockscans/${scanType}.drc`);
		if (!dataResponse.ok) {
			return { error: 'Error fetching mock scan data' };
		}
		const data = await dataResponse.blob();
		const uploadResponse = await fetch(createUploadResponse.uploadURL, {
			method: 'PUT',
			body: data
		});
		if (!uploadResponse.ok) {
			return { error: 'Error uploading mock scan data' };
		}
		// complete scan
		try {
			await AgentService.agentControllerCompleteScanUpload(createUploadResponse.measurement_id);
		} catch (e) {
			console.log(e);
			return { error: 'Error finalizing mock scan' };
		}
		window.postMessage(
			{
				event: isLeftFoot ? '3Dleftfoot' : '3Drightfoot',
				data: 'finished'
			},
			window.origin
		);
		return {};
	}

	public async startScan(name: string) {
		if (!this.lastValidSession) {
			throw new Error('[AGENT] Last valid session not found');
		}
		if (!this.currentDevice) {
			throw new Error('[AGENT] No current device found');
		}
		if (useSoftwareMock()) {
			return await this.debugPerformMockScan(name);
		}

		let startScan;
		const uid: string = this.sessionUUID ? this.sessionUUID : '';

		const scanMode = this.store.agent.scanMode.toUpperCase().split('-').join('_');

		const data = {
			session_id: this.lastValidSession,
			name,
			scan_mode: this.store.agent.scanMode === 'non-weighted' ? 'SEMI_WEIGHTED' : scanMode,
			body_part: this.store.agent.currentDevice.model_id.includes('iQUBE') ? 'FOOT' : 'FOOT_AND_ANKLE',
			product: 'INSOLE',
			foot: name === 'leftscan' ? 'left' : 'right',
			side: 'BOTH',
			location_id: this.store.engine.store.getStore.storeID
		};

		if (!this.store.auth.token) {
			throw new Error('[AGENT] No JWT');
		}

		startScan = await fetch(`http://${this.deviceURI}:${this.agentAPIPort}/hpagent/api/v1/scanner/${this.currentDevice.id}/scan`, {
			headers: {
				UUID: uid,
				'Content-Type': 'application/json',
				authorization: this.store.auth.token
			},
			method: 'POST',
			body: JSON.stringify(data)
		}).catch((e) => e);
		await this.store._patient.updateMedicalProfile({ update: true });
		return startScan.json();
	}

	public async healthCheck() {
		if (useSoftwareMock()) {
			return { status: 'OK' };
		}
		const health = await fetch(`http://${this.deviceURI}:${this.agentAPIPort}/hpagent/api/v1/health`, {
			method: 'GET'
		}).catch((e) => e);
		return health.json();
	}

	public async open(): Promise<HPAgentStatus> {
		if (useSoftwareMock()) {
			this.agentStatus = HPAgentStatus.OPENED;
			this.devices = [
				{
					name: 'Mock Scanner (0x12345678)',
					id: '0x12345678',
					model: 'Mock Scanner',
					type: 'SCANNER',
					version: '1.6.0',
					serial: '0x12345678',
					firmware: '1.0.0',
					state: 'READY',
					model_id: 'N/A'
				}
			];
			return this.agentStatus;
		}
		if (this.agentStatus !== HPAgentStatus.DISCONNECTED) {
			throw new Error('Agent is connected, disconnect before openning another session.');
		}

		this.disconnect();
		this.agentStatus = HPAgentStatus.CONNECTING;

		if (!this.store.auth.token) {
			throw new Error('[AGENT] JWT not found while openning session.');
		}

		const protocol = this.store.auth.token;

		this.socket = new WebSocket(`ws://${this.deviceURI}:${this.agentAPIPort}/hpagent`, protocol);

		this.socket.addEventListener('open', (msg) => {
			if (msg.type === 'open') {
				this.agentStatus = HPAgentStatus.OPENED;
				window.dispatchEvent(new CustomEvent('HPAgentOpen'));
			}
		});

		this.socket.addEventListener('close', (msg) => {
			this.agentStatus = HPAgentStatus.DISCONNECTED;
			window.dispatchEvent(new CustomEvent('agentDisconnected'));
		});

		this.socket.addEventListener('message', (msg) => {
			const data = JSON.parse(msg.data);

			if (data.targetUUID && data.targetUUID !== this.sessionUUID) {
				console.warn('Got message for wrong target ', data.targetUUID, this.sessionUUID);
				return;
			}

			window.dispatchEvent(new CustomEvent(data.event, { detail: data.data }));

			if (data.event === 'OpenSessionStatus') {
				this.sessionUUID = data.data.uuid;
				this.agentStatus = HPAgentStatus.OPENED;
			}

			if (data.event === 'OpenSessionFailed') {
				this.sessionUUID = undefined;
				this.agentStatus = HPAgentStatus.SESSION_FAILED;
			}

			if (data.event === 'OnDeviceAttached') {
				this.devices.push(data.data);
			}

			if (data.event === 'OnDeviceAvailable') {
				this.devices = data.data;
			}

			if (data.event === 'OnDeviceDetached') {
				this.devices = this.devices.filter((d) => d.id !== data.data.id);
				window.dispatchEvent(new CustomEvent('scannerDetached'));
			}
			if (data.event === 'OnDeviceStateChanged') {
				console.log('Detached', data.event);
				if (typeof data.data.state === 'string') {
					if (data.data.state.toLowerCase() === 'detached') {
						this.agentStatus = HPAgentStatus.DETACHED;
						window.dispatchEvent(new CustomEvent('scannerDetached'));
					}
				}
			}
		});

		this.socket.addEventListener('error', (msg) => {
			window.dispatchEvent(new CustomEvent('socketError'));
		});

		await Promise.race([this.waitForStatus(HPAgentStatus.OPENED), this.waitForStatus(HPAgentStatus.DISCONNECTED)]);
		this.sendTelemetryData(this.store.engine.store.getStore.storeID);
		return this.agentStatus;
	}

	public triggerDownload() {
		const message = {
			event: 'SoftpaqUpdater',
			data: {
				action: 'DownloadSoftpaq'
			}
		};
		this.socket?.send(JSON.stringify(message));
	}

	public runInstaller() {
		const message = {
			event: 'SoftpaqUpdater',
			data: {
				action: 'RunInstaller'
			}
		};
		this.socket?.send(JSON.stringify(message));
	}

	public sendTelemetryData(siteID: string) {
		const message = {
			event: 'SendTelemetryData',
			data: {
				site_id: siteID
			}
		};

		this.socket?.send(JSON.stringify(message));
	}

	public checkUpdate() {
		const message = {
			event: 'SoftpaqUpdater',
			data: {
				action: 'CheckUpdate',
				jwt: this.store.auth.token
			}
		};
		this.socket?.send(JSON.stringify(message));
	}

	public cancelUpdate() {
		const message = {
			event: 'SoftpaqUpdater',
			data: {
				action: 'CancelUpdate'
			}
		};
		this.socket?.send(JSON.stringify(message));
	}

	public disconnect() {
		if (this.socket) {
			this.socket.close();
			this.agentStatus = HPAgentStatus.DISCONNECTED;
		}
	}

	public async waitForStatus(status: HPAgentStatus) {
		for (let i = 0; i < 100; i++) {
			if (status === this.agentStatus) {
				return true;
			}
			await new Promise((r) => {
				setTimeout(r, 1000);
			});
		}
		return false;
	}
}

interface Device {
	name: string;
	id: string;
	model: string;
	type: string;
	version: string;
	serial: string;
	firmware: string;
	state: string;
	model_id: string;
}

enum HPAgentStatus {
	DISCONNECTED,
	CONNECTING,
	OPENED,
	SESSION_CREATION,
	SESSION_CREATED,
	SESSION_FAILED,
	ERROR,
	DETACHED
}
